import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseServiceKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

// Prevent UI from getting stuck in a loading state if a network request hangs.
// This applies to all Supabase requests (auth + database + storage).
const DEFAULT_SUPABASE_FETCH_TIMEOUT_MS = 60000;

const fetchWithTimeout: typeof fetch = async (input, init) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_SUPABASE_FETCH_TIMEOUT_MS);

  // Respect any upstream abort signal (e.g. navigation/unmount aborts).
  if (init?.signal) {
    if (init.signal.aborted) {
      controller.abort();
    } else {
      init.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
};

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing required Supabase environment variables');
}

type SupabaseSingletonState = {
  supabase?: SupabaseClient;
  supabaseAdmin?: SupabaseClient;
  authSubscription?: { unsubscribe: () => void };
};

const singletonState = ((globalThis as any).__ballymarketSupabase ??= {}) as SupabaseSingletonState;

if (!singletonState.supabase) {
  singletonState.supabase = createClient(supabaseUrl, supabaseKey, {
    global: {
      fetch: fetchWithTimeout,
    },
    auth: {
      persistSession: true,
      // Let the app load immediately with cached session.
      // Token refresh will happen in the background via onAuthStateChange.
      autoRefreshToken: true,
      // Disable URL detection to avoid blocking on URL parsing during init.
      // This can cause hangs if the URL contains auth fragments that need processing.
      detectSessionInUrl: false,
      // Reduce storage access timeout for faster init
      flowType: 'pkce',
    },
  });
}

if (!singletonState.supabaseAdmin) {
  // Create admin client with service role key for admin operations.
  // If the service role key is missing, fall back to anon key and log a warning.
  // This prevents silent hangs in production when the env var is not set.
  if (!supabaseServiceKey) {
    console.warn(
      '[supabase] VITE_SUPABASE_SERVICE_ROLE_KEY is not set. ' +
        'Admin operations will use the anon key and may fail due to RLS policies.'
    );
  }

  singletonState.supabaseAdmin = createClient(supabaseUrl, supabaseServiceKey || supabaseKey, {
    global: {
      fetch: fetchWithTimeout,
    },
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'sb-ballymarket-admin-auth',
    },
  });
}

export const supabase = singletonState.supabase;
export const supabaseAdmin = singletonState.supabaseAdmin;

// Add error handling for connection (only once per browser context)
if (!singletonState.authSubscription) {
  const { data: authListener } = supabase.auth.onAuthStateChange((event, _session) => {
    if (event === 'SIGNED_OUT') {
      console.log('User signed out');
    }
  });
  singletonState.authSubscription = authListener.subscription;

  // Ensure we don't leak multiple auth listeners during Vite hot reloads.
  const hot = (import.meta as any).hot;
  if (hot) {
    hot.dispose(() => {
      singletonState.authSubscription?.unsubscribe();
      singletonState.authSubscription = undefined;
    });
  }
}
